'use strict';

angular.module('ClinicApp')
  .factory('NotificationService', (
    ClinicConfig,
    OCLogUtil,
    $timeout,
    $http,
  ) => {
    const NOTIFICATION_TYPE = 'in-app';

    const NOTIFICATION_EVENT_TYPE = {
      RECEIVED: 'received',
      OPENED: 'opened',
    };

    const TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
    });

    class NotificationNotifier {
      #notificationShownListeners;

      #notificationClearedListeners;

      constructor() {
        this.#notificationShownListeners = new Set();
        this.#notificationClearedListeners = new Set();
      }

      addNotificationShownListener = (listener) => {
        this.#notificationShownListeners.add(listener);
      };

      removeNotificationShownListener = (listener) => {
        this.#notificationShownListeners.delete(listener);
      };

      addNotificationClearedListener = (listener) => {
        this.#notificationClearedListeners.add(listener);
      };

      removeNotificationClearedListener = (listener) => {
        this.#notificationClearedListeners.delete(listener);
      };

      onNotificationShown = (notificationId) => {
        this.#notificationShownListeners.forEach((listener) => listener(notificationId));
      };

      onNotificationCleared = (notificationId) => {
        this.#notificationClearedListeners.forEach((listener) => listener(notificationId));
      };
    }

    class NotificationService {
      #notifications;

      #notifier;

      #token;

      constructor() {
        this.#notifications = [];
        this.#notifier = new NotificationNotifier();
      }

      setToken = (token) => {
        this.#token = token;
      };

      onNotificationShown = (listener) => {
        this.#notifier.addNotificationShownListener(listener);
      };

      onNotificationCleared = (listener) => {
        this.#notifier.addNotificationClearedListener(listener);
      };

      /**
     * Adds an active notification.
     * Returns a notification id that can be used to clear the notification.
     * @param {Object} notification - The notification to add.
     * @param {number} [timeout] - The timeout in milliseconds to automatically clear the notification.
     * @returns {string} The notification id.
     */
      addNotification = (notification, timeout) => {
        const frozenNotification = Object.freeze({
          id: this.#generateNotificationId(),
          title: notification.title,
          body: notification.body,
          icon: notification.icon,
          data: {
            ...notification.data,
            timestamp: this.#getTimestamp(notification),
          },
        });

        this.#notifications.push(frozenNotification);
        this.#notifier.onNotificationShown(frozenNotification.id);
        if (timeout) {
          $timeout(() => this.clearNotification(frozenNotification.id), timeout);
        }

        return frozenNotification.id;
      };

      getNotifications = () => Object.freeze([...this.#notifications]);

      clearNotification = (notificationId) => {
        const index = this.#notifications
          .findIndex((notification) => notification.id === notificationId);
        if (index === -1) {
          return;
        }

        this.#notifications.splice(index, 1);
        this.#notifier.onNotificationCleared(notificationId);
      };

      trackNotificationShown = async (
        notification,
        clinicId,
      ) => {
        const link = notification.data?.link || null;

        OpencareAnalytics.trackPageView({
          pageType: OpencareAnalytics.pageTypes.PRACTICE_NOTIFICATION,
          practiceId: clinicId,
          notificationUrl: link,
        });

        await this.#trackNotification(notification, NOTIFICATION_EVENT_TYPE.RECEIVED);
      };

      trackNotificationClicked = async (
        notification,
        clinicId,
      ) => {
        const link = notification.data?.link || null;
        const historyId = notification.data?.historyId || null;

        OpencareAnalytics.trackClick({
          pageType: OpencareAnalytics.pageTypes.PRACTICE_NOTIFICATION,
          practiceId: clinicId,
          notificationUrl: link,
          historyId,
        });

        await this.#trackNotification(notification, NOTIFICATION_EVENT_TYPE.OPENED);
      };

      #generateNotificationId = () =>
        Math.random()
          .toString(16)
          .slice(2);

      #getTimestamp = (notification) => {
        if (!notification.data) {
          return null;
        }

        const timestampInt = parseInt(notification.data.time, 10);
        if (isNaN(timestampInt)) {
          return null;
        }

        const date = new Date(timestampInt);
        return TIME_FORMAT.format(date);
      };

      #trackNotification = async (
        notification,
        eventType,
      ) => {
        const historyId = notification.data?.historyId || null;

        try {
          return await $http.post(
            `${ClinicConfig.HOSTNAME}/notification/track`,
            {
              token: this.#token,
              notificationType: NOTIFICATION_TYPE,
              eventType,
              notification,
              historyId,
            },
          );
        } catch (error) {
          OCLogUtil.rollbar.error(
            'Failed to track notification',
            {
              error,
              notification,
            },
          );
        }
      };
    }

    return new NotificationService();
  });
